import { Box, Flex } from 'reflexbox';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useLocation } from 'react-router';

import {
	Avatar,
	Card,
	Color,
	Heading,
	Join,
	Paragraph,
	Progress,
	Radio,
	Skeleton,
	Subheading,
} from '@treams/treams-ui';

import * as graphql from 'generated/graphql';
import useLocalize from 'packages/kakapo-ui-application/hooks/useLocalize';
import { useModalLink } from 'navigation/ModalNavigation';
import { FeasibilityTag } from './Goal';

function GoalOwner(props: { owner: graphql.GoalOwner }) {
	const { t } = useTranslation();

	if (props.owner.__typename === 'User') {
		return (
			<Flex mt={2} alignItems="center">
				<Avatar user={props.owner} size={24} mr={2} />
				<Paragraph flexGrow={1} flexShrink={1}>
					{props.owner.firstName} {props.owner.lastName}
				</Paragraph>
			</Flex>
		);
	}

	if (props.owner.__typename === 'Group') {
		return (
			<Flex mt={2} alignItems="center">
				<Avatar.Group group={props.owner} size={24} mr={2} />
				<Paragraph flexGrow={1} flexShrink={1}>
					{props.owner.name}
				</Paragraph>
			</Flex>
		);
	}

	return (
		<Flex mt={2} alignItems="center">
			<Avatar.Group size={24} mr={2} />
			<Paragraph flexGrow={1} flexShrink={1}>
				{t('goals.card.owner.organization')}
			</Paragraph>
		</Flex>
	);
}

const GoalCard = Skeleton(
	function GoalCard(props: {
		user?: graphql.User;
		goal: graphql.Goal | graphql.GoalRevision;
		modal?: boolean;
		small?: boolean;
		readonly?: boolean;
		selected?: boolean;
		owner?: boolean;
		onClick?: () => void;
		onSelect?: () => void;
	}) {
		const { t } = useTranslation();

		const { localize } = useLocalize();

		const isDue =
			moment(props.goal.dueDate).isBefore(moment()) &&
			props.goal.status === graphql.GoalStatus.Active;

		let regularLink =
			props.goal.__typename === 'GoalRevision'
				? `/goals/${props.goal.goalID}/revisions/${props.goal.revision}`
				: props.user
				? `/users/${props.user.id}/goals/${props.goal.id}`
				: `/goals/${props.goal.id}`;

		if (props.goal.__typename === 'GoalRevision' && props.goal.secret) {
			regularLink += `?secret=${props.goal.secret}`;
		}

		const link = useModalLink(regularLink, props.modal ?? false);
		const returnURL = useLocation().pathname.replace('/v2', '');

		return (
			<Card
				link={
					props.onClick || props.onSelect || props.readonly
						? undefined
						: props.goal.id
						? link
						: undefined
				}
				navigationState={{ returnURL }}
				onClick={props.onClick ?? props.onSelect}
				selected={props.selected}
				footer={
					props.owner !== false && 'owner' in props.goal && <GoalOwner owner={props.goal.owner} />
				}
				opacity={props.goal.isArchived ? 0.5 : 1}
			>
				<Flex alignItems="center">
					{props.onSelect && (
						<Box flexGrow={0} flexShrink={0} mr={3}>
							<Radio onChange={() => props.onSelect?.()} checked={props.selected} />
						</Box>
					)}
					<Box flexGrow={1} flexShrink={1}>
						<Flex alignItems="center" justifyContent="space-between">
							{(props.goal.dueDate ||
								props.goal.completedAt ||
								props.goal.archivedAt ||
								props.goal.createdAt) &&
								!props.small && (
									<Paragraph mb={props.small ? 0 : 1}>
										<Join separator={<>&nbsp;{'·'}&nbsp;</>}>
											{props.goal.dueDate && (
												<Color color={isDue ? 'red' : 'slateBlue'}>
													{t('goals.card.dueDate', {
														date: moment(props.goal.dueDate).format('L'),
													})}
												</Color>
											)}
											{props.goal.completedAt ? (
												<span>
													{t('goals.card.completedAt', {
														date: moment(props.goal.completedAt).format('L'),
													})}
												</span>
											) : (
												props.goal.createdAt && (
													<span>
														{t('goals.card.createdAt', {
															date: moment(props.goal.createdAt).format('L'),
														})}
													</span>
												)
											)}
											{props.goal.archivedAt && (
												<span>
													{t('goals.card.archivedAt', {
														date: moment(props.goal.archivedAt).format('L'),
													})}
												</span>
											)}
										</Join>
									</Paragraph>
								)}

							{props.goal.feasibility && !props.small && (
								<Box mr={2}>
									<FeasibilityTag label="" feasibility={props.goal.feasibility} plain />
								</Box>
							)}
						</Flex>

						{props.small ? (
							<Box>
								<Subheading flexGrow={1} flexShrink={1}>
									{localize(props.goal.title).text}
								</Subheading>
								<Progress mt={2} flexGrow={0} flexShrink={0} value={props.goal.progress} />
							</Box>
						) : (
							<>
								<Heading>{localize(props.goal.title).text}</Heading>
								<Progress mt={3} value={props.goal.progress} />
							</>
						)}
					</Box>
				</Flex>
			</Card>
		);
	},
	function GoalCardSkeleton(props: { small?: boolean }) {
		return (
			<Card.Skeleton>
				{!props.small && <Paragraph.Skeleton mb={1} lines={1} />}
				<Heading.Skeleton />
				{!props.small && <Progress.Skeleton mt={3} />}
			</Card.Skeleton>
		);
	},
);

export default GoalCard;
